// ────────────────────────────────────────────────────
// src/features/cashRegister/components/CashFlowAdmin.tsx
// ────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import MinimalisticTable, {
  ColumnDef,
} from '../../../../shared/components/ui/MinimalisticTable';
import { useCashRegisters } from '../hooks/useCashRegisters';
import { CashRegister } from '../api/cashFlowApi';

/* ───── types ─────────────────────────────────────── */
type Row =
  | (CashRegister & { __isNew?: false })
  | ({ id_register: -1; __isNew: true } & CashRegister);

/* ───── component ─────────────────────────────────── */
export default function CashFlowAdmin() {
  const {
    data: allRegs = [],
    isLoading,
    isError,
    error,
    addMut,
    toggleMut,
    delMut,
  } = useCashRegisters();

  /* inline‑add ‑––––––––––––––––––––––– */
  const [isAdding, setIsAdding] = useState(false);
  const [newName,  setNewName]  = useState('');

  /* helpers ‑–––––––––––––––––––––––––– */
  const isBusy = useCallback(
    (id: number) =>
      (toggleMut.isPending && toggleMut.variables?.id === id) ||
      (delMut.isPending    && delMut.variables     === id),
    [toggleMut, delMut],
  );

  const handleCreate  = () => addMut.mutate(newName.trim());
  const handleToggle  = (id: number, cur: boolean) =>
    id > 0 && toggleMut.mutate({ id, active: !cur });
  const handleDelete = (id: number) => {
    if (id > 0) delMut.mutate(id);
  };

  /* data – only active rows + phantom new‑row ‑–––– */
  const rows: Row[] = useMemo(() => {
    const base = [...allRegs].sort((a, b) =>
      Number(b.is_active) - Number(a.is_active),
    ); // active first, then inactive
    if (!isAdding) return base;
    return [
      {
        id_register: -1,
        register_name: '',
        is_active: true,
        created_at: '',
        __isNew: true,
      },
      ...base,
    ];
  }, [allRegs, isAdding]);

  /* columns (memo to avoid re‑render churn) ‑–––––– */
  const columns: ColumnDef<Row>[] = useMemo(
    () => [
      { header: 'ID', accessor: 'id_register' },

      {
        header  : 'Name',
        accessor: 'register_name',
        Cell: (_val, row) =>
          row.__isNew ? (
            <input
              className="w-full bg-neutral-800 border border-neutral-700 rounded p-1 text-neutral-100"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Register name…"
              autoFocus
            />
          ) : (
            <span>{row.register_name || '—'}</span>
          ),
      },

      {
        header   : 'Active',
        accessor : 'is_active',
        Cell     : (v: boolean) => (v ? 'Yes' : 'No'),
      },

      {
        header  : 'Actions',
        accessor: 'id_register',
        Cell    : (id: number, row) => {
          /* ── placeholder row ───────────────────── */
          if (row.__isNew) {
            const busy = addMut.isPending;
            return (
              <div className="flex space-x-2">
                <button
                  disabled={busy || !newName.trim()}
                  onClick={handleCreate}
                  className="text-emerald-500 hover:text-emerald-400"
                >
                  {busy ? 'Creating…' : 'Create'}
                </button>
                <button
                  onClick={() => {
                    setIsAdding(false);
                    setNewName('');
                  }}
                  className="text-rose-500 hover:text-rose-400"
                >
                  Cancel
                </button>
              </div>
            );
          }

          /* ── normal row ────────────────────────── */
          const busy = isBusy(id);

          return (
            <div className="flex items-center space-x-4">
              {/* enable / disable */}
              <button
                disabled={busy}
                onClick={() => handleToggle(id, row.is_active)}
                className={
                  row.is_active
                    ? 'text-rose-400 hover:text-rose-300'
                    : 'text-emerald-400 hover:text-emerald-300'
                }
              >
                {busy ? 'Saving…' : row.is_active ? 'Deactivate' : 'Activate'}
              </button>

              {/* hard delete */}
              <button
                title="Delete register"
                className="text-neutral-500 hover:text-rose-500"
                disabled={busy}
                onClick={() => handleDelete(id)}
              >
                <Trash2 size={18} />
              </button>
            </div>
          );
        },
      },
    ],
    [addMut.isPending, isBusy, newName],
  );

  /* render ‑–––––––––––––––––––––––––––– */
  if (isLoading) return <p className="text-neutral-400">Loading registers…</p>;
  if (isError)   return <p className="text-rose-400">Error: {error?.message}</p>;

  return (
    <div className="bg-neutral-900 p-4 rounded-md">
      <h2 className="text-lg font-semibold mb-4 text-neutral-200">
        Register Catalogue
      </h2>

      <MinimalisticTable<Row>
        columns={columns}
        data={rows}
        noDataMessage="No registers found."
        showNewButton={!isAdding}
        onNew={() => setIsAdding(true)}
        editableAccessors={['register_name']}
        newRowValues={{ register_name: newName }}
        onNewRowChange={(acc, val) =>
          acc === 'register_name' && setNewName(String(val))
        }
        onAcceptNew={handleCreate}
        onCancelNew={() => {
          setIsAdding(false);
          setNewName('');
        }}
      />
    </div>
  );
}
